import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { useRoomContext } from '@src/contexts';
import { useWebinarRoomContext } from '@src/contexts/webinar';

import { usePeerActions } from '@src/hooks';
import useWebinarRecording from '@src/hooks/webinar/useWebinarRecording';

import SocketService from '@src/services/SocketService';

import { usePeersStore, useRoomActionsStore, useRoomStore, useWebinarRoomStore } from '@src/stores';
import { handleExitOrEndWebinar } from '@src/utils';

import { EWebinarRoutes } from '@src/constants';

import { EUserType, EWebinarEventsTypes } from '@src/types';

const useWebinarRoom = () => {
  const { currentPeer, currentPeerType, setHasUsedShareAccess, setIsLoading } = useRoomContext();
  const navigate = useNavigate();
  const trialLockTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { leaveChannel } = useWebinarRoomContext();

  const { handleUpdatePeerInfo } = usePeerActions(currentPeer);

  const { requestInfo, meetingType, setIsJoinedRoom } = useRoomStore(
    useShallow((state) => ({
      meetingType: state.meetingType,
      requestInfo: state.requestInfo,
      setIsJoinedRoom: state.setIsJoinedRoom,
    }))
  );
  const {
    setMicSettings,
    setCamSettings,
    setScreenRecordingSettings,
    setRaiseHandSettings,
    setShareScreenSettings,
    setExitRoomSettings,
  } = useRoomActionsStore(
    useShallow((state) => ({
      setMicSettings: state.setMicSettings,
      setCamSettings: state.setCamSettings,
      setRaiseHandSettings: state.setRaiseHandSettings,
      setShareScreenSettings: state.setShareScreenSettings,
      setScreenRecordingSettings: state.setScreenRecordingSettings,
      setExitRoomSettings: state.setExitRoomSettings,
    }))
  );

  const { startRecording, stopRecording, mediaRecorderRef, forceStopAllRecordings } = useWebinarRecording();
  const { updatePeerInfo } = usePeersStore(
    useShallow((state) => ({
      updatePeerInfo: state.updatePeerInfo,
    }))
  );

  const { setShareScreenStreamCredentials } = useWebinarRoomStore(
    useShallow((state) => ({
      setShareScreenStreamCredentials: state.setShareScreenStreamCredentials,
    }))
  );

  const SHARE_SCREEN_LOCK_KEY = 'shareScreenTrialLockEndTime';

  const setTrialLock = useCallback(
    (lockEndTime: number) => {
      setShareScreenSettings({ trialLockEndTime: lockEndTime });
      localStorage.setItem(SHARE_SCREEN_LOCK_KEY, String(lockEndTime));

      if (trialLockTimeoutRef.current) {
        clearTimeout(trialLockTimeoutRef.current);
      }

      const remaining = lockEndTime - Date.now();
      if (remaining > 0) {
        trialLockTimeoutRef.current = setTimeout(() => {
          setShareScreenSettings({ trialLockEndTime: null });
          localStorage.removeItem(SHARE_SCREEN_LOCK_KEY);
          trialLockTimeoutRef.current = null;
        }, remaining);
      }
    },
    [setShareScreenSettings]
  );

  // Restore trial lock from localStorage on mount (survives page refresh)
  useEffect(() => {
    const stored = localStorage.getItem(SHARE_SCREEN_LOCK_KEY);
    if (stored) {
      const lockEndTime = Number(stored);
      const remaining = lockEndTime - Date.now();
      if (remaining > 0) {
        setTrialLock(lockEndTime);
      } else {
        localStorage.removeItem(SHARE_SCREEN_LOCK_KEY);
      }
    }
  }, [setTrialLock]);

  useEffect(() => {
    const onMicClickCallback = (isMuted: boolean) => {
      if (currentPeer) {
        const { peer_id, peer_name } = currentPeer.peer_info || {};
        updatePeerInfo(peer_id, { peer_audio: !isMuted });
        handleUpdatePeerInfo({ peer_name, peer_id, type: 'audio', status: !isMuted });
      }
    };

    setMicSettings({ onMicClickCallback });
  }, [currentPeer, handleUpdatePeerInfo, setMicSettings, updatePeerInfo]);

  useEffect(() => {
    const onCamClickCallback = async (isClosed: boolean): Promise<void> => {
      if (currentPeer) {
        const { peer_id, peer_name } = currentPeer.peer_info || {};
        setCamSettings({ isCamClosed: isClosed });
        updatePeerInfo(currentPeer.peer_info.peer_id, { peer_video: !isClosed });
        handleUpdatePeerInfo({ peer_name, peer_id, type: 'video', status: !isClosed });
      }
    };

    const onClickCallback = (isRaised: boolean) => {
      const { peer_id, peer_name } = currentPeer?.peer_info || {};

      if (!peer_id || !peer_name) {
        return;
      }

      handleUpdatePeerInfo({ peer_id, peer_name, type: 'hand', status: isRaised });
    };

    setRaiseHandSettings({ onClickCallback });
    setCamSettings({ onCamClickCallback });
  }, [currentPeer, handleUpdatePeerInfo, setCamSettings, setRaiseHandSettings, updatePeerInfo]);

  useEffect(() => {
    const onClickCallback = async (isActive: boolean) => {
      try {
        if (!isActive) {
          setShareScreenSettings({ isActive: false, isLoading: false });
          return;
        }

        // Set loading to true when starting screen share (webinar-only)
        setShareScreenSettings({ isLoading: true });

        //If webinar and current user is speaker, we need to send the requestId stored when receiving the admitAction -> APPROVED event
        SocketService.request(EWebinarEventsTypes.SHARE_SCREEN, {
          requestId: currentPeerType === EUserType.SPEAKER ? requestInfo[0].requestId : null,
        })
          .then((response) => {
            if (response?.success) {
              setShareScreenSettings({ isActive: isActive });

              // (hasUsedShareAccess) was set here because of a bug: when the speaker clicks cancel share (he clicks but doesn't actually share),
              // he can't share again because then the backend will return false to the same request Id, so in webinar,
              // because of this extra validation step, I can't let him keep the access once he clicked the button and got validated
              // Backend can't let me request validation with the same requestId again.
              setHasUsedShareAccess(true);
              if (response?.streamCredential) {
                setShareScreenStreamCredentials(response.streamCredential);
              }

              // Set 30-second lock after successful share trial (persisted across refresh)
              setTrialLock(Date.now() + 30000);
            } else {
              // If backend validation fails, clear loading state
              setShareScreenSettings({ isLoading: false });
            }
            return;
          })
          .catch((err) => {
            console.error('Share screen validation failed: ', err);
            // Clear loading state on error
            setShareScreenSettings({ isLoading: false });
          });
      } catch (err) {
        console.error('[ShareScreen] toggle failed:', err);
        setShareScreenSettings({ isActive: false, isLoading: false });
      }
    };

    setShareScreenSettings({ onClickCallback });

    return () => {
      setShareScreenSettings({ onClickCallback: null });
    };
  }, [
    currentPeer,
    currentPeerType,
    handleUpdatePeerInfo,
    meetingType,
    requestInfo,
    setHasUsedShareAccess,
    setShareScreenSettings,
    setShareScreenStreamCredentials,
    setTrialLock,
    updatePeerInfo,
  ]);

  // Cleanup trial lock timeout on unmount only
  useEffect(() => {
    return () => {
      if (trialLockTimeoutRef.current) {
        clearTimeout(trialLockTimeoutRef.current);
        trialLockTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const onClickCallback = async (isRecording: boolean) => {
      try {
        if (isRecording) {
          await startRecording();
        } else {
          if (mediaRecorderRef.current) {
            stopRecording();
          } else {
            forceStopAllRecordings();
          }
        }
      } catch (err) {
        console.error('Recording toggle failed', err);
      }
    };

    setScreenRecordingSettings({ onClickCallback });
  }, [setScreenRecordingSettings, startRecording, stopRecording, mediaRecorderRef, forceStopAllRecordings]);

  useEffect(() => {
    const onExitCallback = async (endSessionForAll = false, isBanned = false) => {
      let path;
      if (isBanned) {
        path = EWebinarRoutes.RESTRICTED_ACCESS;
      } else if (endSessionForAll) {
        path = EWebinarRoutes.END;
      } else {
        path = EWebinarRoutes.REJOIN;
      }
      handleExitOrEndWebinar({
        endSessionForAll,
        redirect: navigate,
        path,
      });
      if (leaveChannel) {
        await leaveChannel();
      }
    };

    setExitRoomSettings({ onExitCallback });
  }, [leaveChannel, navigate, setExitRoomSettings, setIsJoinedRoom, setIsLoading]);
};

export default useWebinarRoom;
